import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PlannerTab } from 'features/Yucollect/AgencyMonitor/types/AllocationListDataType';
import { Column } from 'features/Yucollect/utils/types';

import {
  AllocationRecord,
  IChannelPlannerMetricsRequest,
  IChannelPlannerMetricsResponse,
  IFailedPayload,
  IGetAllocationDashboardRequest,
  IGetAllocationDetailRequest,
  IGetAllocationDetailsResponse,
  IGetHeadersRequest,
  IGetHeadersResponse,
  IGetRepeatAllocationDashboardRequest,
  IGetRepeatAllocationDashboardResponse,
  IGetSearchInDataGridRequest,
  IGetSummaryRequest,
  IGetSummaryResponse,
  IGetUniqueValuesByColumnRequest,
  IGetUniqueValuesByColumnResponse,
  IHeaders,
  ISearchInFilterByValuesRequest,
  ISearchInFilterByValuesResponse,
  ISummary,
  MergeStatus,
} from '../../types/Allocation.types';

export type AddNewAccountsType = 'success' | 'error' | null;

export interface AllocationDetailsState {
  data: AllocationRecord[];
  headers: IHeaders[];
  dataGridLoader: boolean;
  headersLoader: boolean;
  summaryLoader: boolean;
  channelMetricsLoader: boolean;
  downloadExcelFileLoader: boolean;
  uniqueValuesLoader: boolean;
  searchLoader: boolean;
  error: IFailedPayload | null;
  uniqueValues: string[];
  filteredResultsByValues: string[];
  bucket: string[] | null;
  channel: string[] | null;
  product: string[] | null;
  pagination: {
    limit: number;
    offset: number;
    page: number;
    pageSize: number;
  };
  allocationId: string | null;
  allocationName: string | null;
  allocationStartDate: string;
  allocationEndDate: string;
  invalid_records: number;
  no_follow_up_record: number;
  total_records: number;
  filters: Column[];
  summary: ISummary;
  mergeHistoricDataStatus: MergeStatus;
  mergeHistoricDataMessage: string;
  channelPlannerMetrics: IChannelPlannerMetricsResponse;
  excelFileURL: string;
  isAutoAssigned: boolean;
  totalRecords: number;
  currentAllocation: Record<string, string>;
  allocationFileRecordIds: string[];
  allocationSummary: object;
  repeatAllocationSummary: IGetRepeatAllocationDashboardResponse;
  activePlannerTab: string;
}

export const allocationDataInitState: AllocationDetailsState = {
  data: [],
  uniqueValues: [],
  filteredResultsByValues: [],
  dataGridLoader: false,
  headersLoader: false,
  uniqueValuesLoader: false,
  summaryLoader: false,
  channelMetricsLoader: false,
  downloadExcelFileLoader: false,
  searchLoader: false,
  error: null,
  bucket: null,
  channel: null,
  product: null,
  pagination: {
    limit: 10,
    offset: 0,
    page: 1,
    pageSize: 10,
  },
  allocationId: null,
  allocationName: null,
  allocationStartDate: '',
  allocationEndDate: '',
  invalid_records: 0,
  no_follow_up_record: 0,
  total_records: 0,
  headers: [],
  filters: [],
  summary: {
    sumWeightedAverage: '0',
    avgOutstanding: '0',
    sumOutstanding: '0',
    numberOfRecords: '0',
  },
  mergeHistoricDataStatus: 'IDLE',
  mergeHistoricDataMessage: '',
  channelPlannerMetrics: {
    Unallocated: {
      sumWeightedAverage: '0',
      avgOutstanding: '0',
      sumOutstanding: '0',
      numberOfRecords: '0',
    },
    Digital: {
      sumWeightedAverage: '0',
      avgOutstanding: '0',
      sumOutstanding: '0',
      numberOfRecords: '0',
    },
    Call: {
      sumWeightedAverage: '0',
      avgOutstanding: '0',
      sumOutstanding: '0',
      numberOfRecords: '0',
    },
    Field: {
      sumWeightedAverage: '0',
      avgOutstanding: '0',
      sumOutstanding: '0',
      numberOfRecords: '0',
    },
  },
  excelFileURL: '',
  isAutoAssigned: false,
  totalRecords: 0,
  currentAllocation: {},
  allocationFileRecordIds: [],
  allocationSummary: {},
  repeatAllocationSummary: {
    channelsConversionData: [],
    dispositionData: [],
    repeatAccountData: [],
  },
  activePlannerTab: PlannerTab.ALL_ACCOUNTS,
};

const allocationDetailsSlice = createSlice({
  name: 'allocationRecordDetails',
  initialState: allocationDataInitState,
  reducers: {
    fetchAllocationRecordsById: (
      state,
      action: PayloadAction<IGetAllocationDetailRequest>
    ) => {
      state.dataGridLoader = true;
      state.error = null;
    },
    fetchAllocationRecordsByIdSuccess: (
      state,
      action: PayloadAction<IGetAllocationDetailsResponse>
    ) => {
      state.data = action.payload.data;
      state.allocationId = action.payload.allocationId;
      state.allocationName = action.payload.allocationName;
      state.allocationStartDate = action.payload.allocationStartDate;
      state.allocationEndDate = action.payload.allocationEndDate;
      state.bucket = action.payload.bucket;
      state.channel = action.payload.channel;
      state.product = action.payload.product;
      state.pagination = action.payload.pagination;
      state.dataGridLoader = false;
      state.totalRecords = action.payload.totalRecords;
    },
    fetchAllocationRecordsByIdFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.dataGridLoader = false;
    },
    // Headers
    fetchAllocationFileHeaders: (
      state,
      action: PayloadAction<IGetHeadersRequest>
    ) => {
      state.headersLoader = true;
      state.error = null;
    },
    fetchAllocationFileHeadersSuccess: (
      state,
      action: PayloadAction<IGetHeadersResponse>
    ) => {
      state.headers = action.payload.data;
      state.headersLoader = false;
    },
    fetchAllocationFileHeadersFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.headersLoader = false;
    },

    // Allocation dashboard
    fetchAllocationDashboard: (
      state,
      action: PayloadAction<IGetAllocationDashboardRequest>
    ) => {
      // state.headersLoader = true;
      state.error = null;
    },
    fetchAllocationDashboardSuccess: (
      state,
      action: PayloadAction<IGetHeadersResponse>
    ) => {
      state.allocationSummary = action.payload.data;
      // state.headersLoader = false;
    },
    fetchAllocationDashboardFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      // state.headersLoader = false;
    },

    // Repeat Allocation dashboard
    fetchRepeatAllocationDashboard: (
      state,
      action: PayloadAction<IGetRepeatAllocationDashboardRequest>
    ) => {
      // state.headersLoader = true;
      state.error = null;
    },
    fetchRepeatAllocationDashboardSuccess: (
      state,
      action: PayloadAction<IGetRepeatAllocationDashboardResponse>
    ) => {
      state.repeatAllocationSummary = action.payload;
      // state.headersLoader = false;
    },
    fetchRepeatAllocationDashboardFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      // state.headersLoader = false;
    },

    // Summary
    fetchAllocationFileSummary: (
      state,
      action: PayloadAction<IGetSummaryRequest>
    ) => {
      state.summaryLoader = true;
      state.error = null;
    },
    fetchAllocationFileSummarySuccess: (
      state,
      action: PayloadAction<IGetSummaryResponse>
    ) => {
      state.summary = action.payload.data;
      state.summaryLoader = false;
    },
    fetchAllocationFileSummaryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.summaryLoader = false;
    },
    getUniqueValuesByColumnName: (
      state,
      action: PayloadAction<IGetUniqueValuesByColumnRequest>
    ) => {
      state.uniqueValuesLoader = true;
      state.error = null;
    },
    getUniqueValuesByColumnNameSuccess: (
      state,
      action: PayloadAction<IGetUniqueValuesByColumnResponse>
    ) => {
      state.uniqueValues = action.payload.data;
      state.uniqueValuesLoader = false;
    },
    getUniqueValuesByColumnNameFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.uniqueValuesLoader = false;
    },
    setAllocationFileRecordIds: (state, action) => {
      state.allocationFileRecordIds = action.payload;
    },

    searchInFilterByValues: (
      state,
      action: PayloadAction<ISearchInFilterByValuesRequest>
    ) => {
      state.searchLoader = true;
      state.error = null;
    },
    searchInFilterByValuesSuccess: (
      state,
      action: PayloadAction<ISearchInFilterByValuesResponse>
    ) => {
      // state.filteredResultsByValues = action.payload;
      state.uniqueValues = action.payload.data;
      state.searchLoader = false;
    },
    searchInFilterByValuesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.searchLoader = false;
    },
    mergeHistoricData: (
      state,
      action: PayloadAction<{ allocationId: string }>
    ) => {
      state.mergeHistoricDataStatus = 'IN_PROGRESS';
      state.error = null;
    },
    mergeHistoricDataSuccess: (state, action) => {
      state.mergeHistoricDataStatus = 'SUCCESS';
      state.searchLoader = false;
      state.mergeHistoricDataMessage = action.payload.message;
    },
    mergeHistoricDataFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.mergeHistoricDataStatus = 'FAILED';
      state.mergeHistoricDataMessage = '';
    },
    resetMergeHistoricDataStatus: (state) => {
      state.mergeHistoricDataStatus = 'IDLE';
    },
    searchInDataGrid: (
      state,
      action: PayloadAction<IGetSearchInDataGridRequest>
    ) => {
      state.dataGridLoader = true;
      state.error = null;
    },
    searchInDataGridSuccess: (
      state,
      action: PayloadAction<IGetAllocationDetailsResponse>
    ) => {
      state.data = action.payload.data;
      state.allocationId = action.payload.allocationId;
      state.allocationName = action.payload.allocationName;
      state.allocationStartDate = action.payload.allocationStartDate;
      state.allocationEndDate = action.payload.allocationEndDate;
      state.bucket = action.payload.bucket;
      state.channel = action.payload.channel;
      state.product = action.payload.product;
      state.pagination = action.payload.pagination;
      state.dataGridLoader = false;
    },
    searchInDataGridFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.dataGridLoader = false;
    },
    // Channel planner metrics
    fetchChannelPlannerMetrics: (
      state,
      action: PayloadAction<IChannelPlannerMetricsRequest>
    ) => {
      state.channelMetricsLoader = true;
      state.error = null;
    },
    fetchChannelPlannerMetricsSuccess: (
      state,
      action: PayloadAction<IChannelPlannerMetricsResponse>
    ) => {
      state.channelPlannerMetrics = action.payload;
      state.channelMetricsLoader = false;
    },
    fetchChannelPlannerMetricsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.channelMetricsLoader = false;
    },
    // Download excel file for allocation + IHS
    downloadAllocationExcelFile: (
      state,
      action: PayloadAction<{ allocationId: string; isValid: boolean }>
    ) => {
      state.downloadExcelFileLoader = true;
      state.error = null;
    },
    downloadAllocationExcelFileSuccess: (
      state,
      action: PayloadAction<string>
    ) => {
      state.excelFileURL = action.payload;
      state.downloadExcelFileLoader = false;
    },
    downloadAllocationExcelFileFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.downloadExcelFileLoader = false;
    },
    resetDownloadAllocationExcelFile: (state) => {
      state.error = null;
      state.downloadExcelFileLoader = false;
      state.excelFileURL = '';
    },

    // set current allocation context
    setCurrentAllocation: (
      state,
      action: PayloadAction<{ allocationId: string; allocationName: string }>
    ) => {
      state.currentAllocation = { ...action.payload };
    },

    // Download excel file for allocation + IHS
    fetchAutoAssignStatus: (
      state,
      action: PayloadAction<{ allocationId: string }>
    ) => {
      state.error = null;
    },
    fetchAutoAssignStatusSuccess: (
      state,
      action: PayloadAction<{ is_auto_allocated: boolean }>
    ) => {
      state.isAutoAssigned = action.payload.is_auto_allocated;
    },
    fetchAutoAssignStatusFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
    },
    setActivePlannerTab: (state, action: PayloadAction<string>) => {
      state.activePlannerTab = action.payload;
    },
  },
});

export const {
  fetchAllocationRecordsById,
  fetchAllocationRecordsByIdSuccess,
  fetchAllocationRecordsByIdFailure,
  fetchAllocationFileHeaders,
  fetchAllocationFileHeadersSuccess,
  fetchAllocationFileHeadersFailure,
  fetchAllocationDashboard,
  fetchAllocationDashboardSuccess,
  fetchAllocationDashboardFailure,
  fetchRepeatAllocationDashboard,
  fetchRepeatAllocationDashboardSuccess,
  fetchRepeatAllocationDashboardFailure,
  fetchAllocationFileSummary,
  fetchAllocationFileSummarySuccess,
  fetchAllocationFileSummaryFailure,
  getUniqueValuesByColumnName,
  getUniqueValuesByColumnNameSuccess,
  getUniqueValuesByColumnNameFailure,
  searchInFilterByValues,
  searchInFilterByValuesSuccess,
  searchInFilterByValuesFailure,
  mergeHistoricData,
  mergeHistoricDataSuccess,
  mergeHistoricDataFailure,
  resetMergeHistoricDataStatus,
  searchInDataGrid,
  searchInDataGridSuccess,
  searchInDataGridFailure,
  fetchChannelPlannerMetrics,
  fetchChannelPlannerMetricsSuccess,
  fetchChannelPlannerMetricsFailure,
  downloadAllocationExcelFile,
  downloadAllocationExcelFileSuccess,
  downloadAllocationExcelFileFailure,
  resetDownloadAllocationExcelFile,
  setCurrentAllocation,
  setAllocationFileRecordIds,
  fetchAutoAssignStatus,
  fetchAutoAssignStatusSuccess,
  fetchAutoAssignStatusFailure,
  setActivePlannerTab,
} = allocationDetailsSlice.actions;

export default allocationDetailsSlice.reducer;
